/**
 * StepEditor — Editor visual de pasos y acciones con drag & drop.
 *
 * Capacidades:
 * - DnD de pasos (reordenamiento via @dnd-kit/sortable)
 * - Añadir/eliminar/editar pasos y acciones
 * - Panel inline de edición de acción DSL (tipo + selector + params)
 * - Validación visual del selector primario
 * - Badge AI/human en cada acción
 */
import React, { useState, useCallback } from 'react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates,
  verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { TestStep, DSLAction, ActionType } from '../../types';
import { ActionEditor } from './ActionEditor';
import { steps as stepsApi } from '../../services/api';

// Normalize backend response (action_type, id) → frontend shape (type, action_id)
function normalizeAction(raw: Record<string, unknown>): import('../../types').DSLAction {
  return {
    action_id: (raw.action_id ?? raw.id ?? String(Date.now())) as string,
    type: (raw.type ?? raw.action_type ?? 'CLICK') as import('../../types').ActionType,
    target: (raw.target ?? { primary: '', fallbacks: [], strategy: 'css' }) as import('../../types').ActionTarget,
    params: (raw.params ?? {}) as Record<string, unknown>,
    on_failure: (raw.on_failure ?? 'abort') as import('../../types').OnFailure,
    meta: (raw.meta ?? { created_by: 'human', description: '', healing_history: [], tags: [] }) as import('../../types').ActionMeta,
  };
}

// ─── Action type metadata for the picker UI ──────────────────────────────────

export const ACTION_GROUPS: { label: string; color: string; types: ActionType[] }[] = [
  {
    label: 'Navegación',
    color: '#3B8BD4',
    types: ['NAVIGATE', 'BACK', 'FORWARD', 'REFRESH'],
  },
  {
    label: 'Interacción',
    color: '#1D9E75',
    types: ['CLICK', 'DOUBLE_CLICK', 'INPUT', 'CLEAR', 'SELECT', 'CHECK', 'UNCHECK', 'HOVER', 'DRAG_DROP'],
  },
  {
    label: 'Teclado',
    color: '#BA7517',
    types: ['KEY_PRESS', 'KEY_COMBO'],
  },
  {
    label: 'Espera',
    color: '#534AB7',
    types: ['WAIT', 'WAIT_FOR', 'WAIT_FOR_URL', 'WAIT_FOR_NETWORK'],
  },
  {
    label: 'Aserciones',
    color: '#993C1D',
    types: ['ASSERT_TEXT', 'ASSERT_VISIBLE', 'ASSERT_HIDDEN', 'ASSERT_VALUE', 'ASSERT_URL', 'ASSERT_TITLE', 'ASSERT_COUNT'],
  },
  {
    label: 'Captura',
    color: '#5F5E5A',
    types: ['SCREENSHOT', 'EXECUTE_SCRIPT', 'SET_VARIABLE', 'HTTP_REQUEST', 'UI_EXTRACT'],
  },
];

const ACTION_COLORS: Record<string, string> = Object.fromEntries(
  ACTION_GROUPS.flatMap(g => g.types.map(t => [t, g.color]))
);

// ─── Sortable Step Card ───────────────────────────────────────────────────────

interface SortableStepProps {
  step: TestStep;
  isExpanded: boolean;
  onToggle: () => void;
  onUpdateStep: (patch: Partial<TestStep>) => Promise<void>;
  onDeleteStep: () => Promise<void>;
  onAddAction: (type: ActionType) => void;
  onUpdateAction: (action: DSLAction) => void;
  onDeleteAction: (action_id: string) => void;
  onReorderActions: (orderedIds: string[]) => Promise<void>;
}

function SortableStep({
  step, isExpanded, onToggle, onUpdateStep, onDeleteStep,
  onAddAction, onUpdateAction, onDeleteAction, onReorderActions,
}: SortableStepProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: step.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  const actionSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleActionDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const actions = step.actions ?? [];
    const oldIdx = actions.findIndex(a => a.action_id === active.id);
    const newIdx = actions.findIndex(a => a.action_id === over.id);
    const reordered = arrayMove(actions, oldIdx, newIdx);
    onReorderActions(reordered.map(a => a.action_id));
  }, [step.actions, onReorderActions]);

  const passedCount = (step.actions ?? []).length;

  return (
    <div ref={setNodeRef} style={style} className="step-card">
      {/* Step header */}
      <div
        className="step-header"
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 16px',
          background: 'var(--step-bg)',
          borderRadius: isExpanded ? '10px 10px 0 0' : 10,
          border: '1px solid var(--step-border)',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        {/* Drag handle */}
        <span
          {...attributes}
          {...listeners}
          onClick={e => e.stopPropagation()}
          style={{
            cursor: 'grab',
            color: 'var(--color-text-tertiary)',
            fontSize: 16,
            padding: '0 4px',
          }}
          title="Arrastrar para reordenar"
        >
          ⠿
        </span>

        {/* Step number badge */}
        <span style={{
          minWidth: 24, height: 24, borderRadius: 6,
          background: 'var(--accent-soft)',
          color: 'var(--accent)',
          fontSize: 11, fontWeight: 600,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {step.order_index + 1}
        </span>

        {/* Description */}
        <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: 'var(--color-text-primary)' }}>
          {step.description || 'Paso sin descripción'}
        </span>

        {/* Action count chip */}
        <span style={{
          padding: '2px 8px', borderRadius: 20,
          background: 'var(--chip-bg)',
          color: 'var(--color-text-secondary)',
          fontSize: 11,
        }}>
          {passedCount} {passedCount === 1 ? 'acción' : 'acciones'}
        </span>

        {/* Expand arrow */}
        <span style={{
          color: 'var(--color-text-tertiary)',
          fontSize: 12,
          transform: isExpanded ? 'rotate(180deg)' : 'none',
          transition: 'transform 0.2s',
        }}>▼</span>
      </div>

      {/* Step body */}
      {isExpanded && (
        <div style={{
          border: '1px solid var(--step-border)',
          borderTop: 'none',
          borderRadius: '0 0 10px 10px',
          background: 'var(--step-body-bg)',
          padding: 16,
        }}>
          {/* Step description edit */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 11, color: 'var(--color-text-tertiary)', fontWeight: 600, letterSpacing: '0.05em' }}>
              DESCRIPCIÓN DEL PASO
            </label>
            <input
              defaultValue={step.description}
              onBlur={e => onUpdateStep({ description: e.target.value })}
              placeholder="Ej: El usuario hace clic en el botón de pago"
              style={{
                width: '100%', marginTop: 6,
                padding: '8px 12px', borderRadius: 8,
                border: '1px solid var(--color-border-secondary)',
                background: 'var(--color-background-primary)',
                color: 'var(--color-text-primary)',
                fontSize: 13,
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Actions list with drag & drop */}
          <DndContext
            sensors={actionSensors}
            collisionDetection={closestCenter}
            onDragEnd={handleActionDragEnd}
          >
            <SortableContext
              items={(step.actions ?? []).map(a => a.action_id)}
              strategy={verticalListSortingStrategy}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(step.actions ?? []).map(action => (
                  <SortableActionRow
                    key={action.action_id}
                    action={action}
                    onEdit={() => onUpdateAction(action)}
                    onDelete={() => onDeleteAction(action.action_id)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {/* Add action button */}
          <ActionTypePicker onSelect={onAddAction} />

          {/* Delete step */}
          <button
            onClick={e => { e.stopPropagation(); onDeleteStep(); }}
            style={{
              marginTop: 14,
              padding: '6px 12px', borderRadius: 8,
              border: '1px solid var(--color-border-secondary)',
              background: 'transparent',
              color: 'var(--color-text-danger)',
              fontSize: 12, cursor: 'pointer',
            }}
          >
            Eliminar paso
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Action Row ───────────────────────────────────────────────────────────────

function ActionRow({ action, dragHandleProps, onEdit, onDelete }: {
  action: DSLAction;
  dragHandleProps?: React.HTMLAttributes<HTMLSpanElement>;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const color = ACTION_COLORS[action.type] ?? '#888';
  const isAI = action.meta.created_by === 'ai';
  const hasHealing = action.meta.healing_history?.length > 0;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '8px 12px', borderRadius: 8,
      border: '1px solid var(--color-border-tertiary)',
      background: 'var(--color-background-secondary)',
    }}>
      {/* Drag handle */}
      <span
        {...dragHandleProps}
        style={{
          cursor: 'grab',
          color: 'var(--color-text-tertiary)',
          fontSize: 14,
          lineHeight: 1,
          padding: '0 2px',
          flexShrink: 0,
        }}
        title="Arrastrar para reordenar"
      >
        ⠿
      </span>

      {/* Action type badge */}
      <span style={{
        padding: '2px 8px', borderRadius: 6,
        background: color + '22',
        color, fontSize: 10, fontWeight: 700,
        letterSpacing: '0.05em', whiteSpace: 'nowrap',
      }}>
        {action.type}
      </span>

      {/* Selector preview */}
      <code style={{
        flex: 1, fontSize: 11,
        color: 'var(--color-text-secondary)',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {action.target?.primary || (action.params as Record<string,unknown>).url as string || '—'}
      </code>

      {/* Badges */}
      {isAI && (
        <span style={{
          padding: '1px 6px', borderRadius: 4,
          background: '#EEEDFE', color: '#534AB7',
          fontSize: 9, fontWeight: 700,
        }}>
          IA
        </span>
      )}
      {hasHealing && (
        <span title="Self-healing aplicado" style={{
          padding: '1px 6px', borderRadius: 4,
          background: '#FAEEDA', color: '#854F0B',
          fontSize: 9, fontWeight: 700,
        }}>
          ✦ HEALED
        </span>
      )}

      {/* on_failure chip */}
      <span style={{
        padding: '1px 6px', borderRadius: 4, fontSize: 9,
        background: action.on_failure === 'abort'
          ? '#FCEBEB' : action.on_failure === 'retry' ? '#E1F5EE' : '#F1EFE8',
        color: action.on_failure === 'abort'
          ? '#A32D2D' : action.on_failure === 'retry' ? '#0F6E56' : '#5F5E5A',
        fontWeight: 600,
      }}>
        {action.on_failure}
      </span>

      {/* Edit / Delete */}
      <button
        onClick={onEdit}
        style={{
          padding: '4px 8px', borderRadius: 6,
          border: '1px solid var(--color-border-secondary)',
          background: 'transparent', fontSize: 11,
          color: 'var(--color-text-secondary)', cursor: 'pointer',
        }}
      >
        Editar
      </button>
      <button
        onClick={onDelete}
        style={{
          padding: '4px 8px', borderRadius: 6,
          border: 'none', background: 'transparent',
          fontSize: 11, color: 'var(--color-text-danger)',
          cursor: 'pointer',
        }}
      >
        ✕
      </button>
    </div>
  );
}

// ─── Sortable Action Row ──────────────────────────────────────────────────────

function SortableActionRow({ action, onEdit, onDelete }: {
  action: DSLAction;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: action.action_id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 10 : 'auto',
      }}
    >
      <ActionRow
        action={action}
        dragHandleProps={{ ...attributes, ...listeners }}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </div>
  );
}

// ─── Action Type Picker ───────────────────────────────────────────────────────

function ActionTypePicker({ onSelect }: { onSelect: (t: ActionType) => void }) {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ position: 'relative', marginTop: 10 }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          width: '100%', padding: '8px 0', borderRadius: 8,
          border: '1px dashed var(--color-border-secondary)',
          background: 'transparent',
          color: 'var(--color-text-tertiary)', fontSize: 13,
          cursor: 'pointer', transition: 'border-color 0.15s',
        }}
      >
        + Añadir acción
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: 'var(--color-background-primary)',
          border: '1px solid var(--color-border-secondary)',
          borderRadius: 10, padding: 12, zIndex: 100,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          display: 'grid', gridTemplateColumns: '1fr 1fr',
          gap: 12, marginTop: 4,
        }}>
          {ACTION_GROUPS.map(group => (
            <div key={group.label}>
              <div style={{
                fontSize: 9, fontWeight: 700, letterSpacing: '0.08em',
                color: group.color, marginBottom: 6, textTransform: 'uppercase',
              }}>
                {group.label}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {group.types.map(type => (
                  <button
                    key={type}
                    onClick={() => { onSelect(type); setOpen(false); }}
                    style={{
                      textAlign: 'left', padding: '4px 8px', borderRadius: 6,
                      border: 'none', background: 'transparent',
                      color: 'var(--color-text-secondary)', fontSize: 11,
                      cursor: 'pointer', transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = group.color + '15')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main StepEditor ──────────────────────────────────────────────────────────

interface StepEditorProps {
  caseId: string;
  initialSteps: TestStep[];
  onStepsChange?: (steps: TestStep[]) => void;
  versionId?: string;
  datasetColumns?: string[];
}

export function StepEditor({ caseId, initialSteps, onStepsChange, versionId, datasetColumns }: StepEditorProps) {
  const [localSteps, setLocalSteps] = useState<TestStep[]>(
    initialSteps.map(s => ({
      ...s,
      // Normalize all actions from any source (DB, AI, cache)
      // so action_id and type are always set correctly
      actions: (s.actions ?? []).map(a => normalizeAction(a as unknown as Record<string, unknown>)),
    }))
  );
  const [expandedStepId, setExpandedStepId] = useState<string | null>(
    initialSteps[0]?.id ?? null
  );
  const [editingAction, setEditingAction] = useState<DSLAction | null>(null);
  const [editingStepId, setEditingStepId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const notifyChange = useCallback((steps: TestStep[]) => {
    setLocalSteps(steps);
    onStepsChange?.(steps);
  }, [onStepsChange]);

  // DnD reorder
  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIdx = localSteps.findIndex(s => s.id === active.id);
    const newIdx = localSteps.findIndex(s => s.id === over.id);
    const reordered = arrayMove(localSteps, oldIdx, newIdx).map((s, i) => ({
      ...s, order_index: i,
    }));
    notifyChange(reordered);
    await stepsApi.reorder(caseId, reordered.map(s => s.id));
  }, [localSteps, caseId, notifyChange]);

  const addStep = async () => {
    const newStep = await stepsApi.create(caseId, {
      description: 'Nuevo paso',
      order_index: localSteps.length,
    });
    const updated = [...localSteps, { ...newStep, actions: [] }];
    notifyChange(updated);
    setExpandedStepId(newStep.id);
  };

  const deleteStep = async (step_id: string) => {
    await stepsApi.delete(step_id);
    notifyChange(localSteps.filter(s => s.id !== step_id));
  };

  const updateStep = async (step_id: string, patch: Partial<TestStep>) => {
    await stepsApi.update(step_id, patch);
    notifyChange(localSteps.map(s => s.id === step_id ? { ...s, ...patch } : s));
  };

  const addAction = async (step_id: string, type: ActionType) => {
    // Find current step to calculate order_index
    const currentStep = localSteps.find(s => s.id === step_id);
    const defaultAction = {
      action_type: type,                          // backend expects action_type, not type
      target: { primary: '', fallbacks: [], strategy: 'css' as const },
      params: { action_type: type, timeout_ms: 5000 },
      on_failure: 'abort' as const,
      meta: { created_by: 'human' as const, description: '', healing_history: [], tags: [] },
      timeout_ms: 5000,                           // required at root level by DSLActionCreate
      order_index: currentStep?.actions?.length ?? 0,  // required by DSLActionCreate
    };
    const rawCreated = await stepsApi.addAction(step_id, defaultAction) as unknown as Record<string, unknown>;
    const created = normalizeAction(rawCreated);
    notifyChange(localSteps.map(s =>
      s.id === step_id ? { ...s, actions: [...s.actions, created] } : s
    ));
    // Abrir editor inline
    setEditingAction(created);
    setEditingStepId(step_id);
  };

  const deleteAction = async (step_id: string, action_id: string) => {
    await stepsApi.deleteAction(step_id, action_id);
    notifyChange(localSteps.map(s =>
      s.id === step_id
        ? { ...s, actions: s.actions.filter(a => a.action_id !== action_id) }
        : s
    ));
  };

  const reorderActions = async (step_id: string, orderedIds: string[]) => {
    await stepsApi.reorderActions(step_id, orderedIds);
    notifyChange(localSteps.map(s => {
      if (s.id !== step_id) return s;
      const actionMap = new Map(s.actions.map(a => [a.action_id, a]));
      return {
        ...s,
        actions: orderedIds.map((id, i) => ({ ...actionMap.get(id)!, order_index: i })),
      };
    }));
  };

  const saveAction = async (updated: DSLAction) => {
    if (!editingStepId) return;
    await stepsApi.updateAction(editingStepId, updated.action_id, updated);
    notifyChange(localSteps.map(s =>
      s.id === editingStepId
        ? { ...s, actions: s.actions.map(a => a.action_id === updated.action_id ? updated : a) }
        : s
    ));
    setEditingAction(null);
    setEditingStepId(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, position: 'relative' }}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={localSteps.map(s => s.id)}
          strategy={verticalListSortingStrategy}
        >
          {localSteps.map(step => (
            <SortableStep
              key={step.id}
              step={step}
              isExpanded={expandedStepId === step.id}
              onToggle={() => setExpandedStepId(v => v === step.id ? null : step.id)}
              onUpdateStep={patch => updateStep(step.id, patch)}
              onDeleteStep={() => deleteStep(step.id)}
              onAddAction={type => addAction(step.id, type)}
              onUpdateAction={action => { setEditingAction(action); setEditingStepId(step.id); }}
              onDeleteAction={action_id => deleteAction(step.id, action_id)}
              onReorderActions={ids => reorderActions(step.id, ids)}
            />
          ))}
        </SortableContext>
      </DndContext>

      {/* Add step button */}
      <button
        onClick={addStep}
        style={{
          padding: '10px 0', borderRadius: 10,
          border: '2px dashed var(--color-border-secondary)',
          background: 'transparent',
          color: 'var(--color-text-tertiary)', fontSize: 13,
          cursor: 'pointer', marginTop: 4,
          transition: 'border-color 0.15s, color 0.15s',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.borderColor = 'var(--accent)';
          e.currentTarget.style.color = 'var(--accent)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.borderColor = 'var(--color-border-secondary)';
          e.currentTarget.style.color = 'var(--color-text-tertiary)';
        }}
      >
        + Añadir paso
      </button>

      {/* Action Editor modal */}
      {editingAction && (
        <ActionEditor
          action={editingAction}
          onSave={saveAction}
          onClose={() => setEditingAction(null)}
          versionId={versionId}
          datasetColumns={datasetColumns}
        />
      )}
    </div>
  );
}